import { create } from 'zustand';

const EMPTY_IDS: string[] = [];

function deriveSelectedTestIds(
  tickedIds: string[],
  browseId: string | null,
  hiddenIds: string[],
): string[] {
  const hiddenSet = new Set(hiddenIds);
  if (tickedIds.length > 0) {
    const visible = tickedIds.filter((id) => !hiddenSet.has(id));
    return visible.length > 0 ? visible : EMPTY_IDS;
  }
  if (browseId && !hiddenSet.has(browseId)) return [browseId];
  return EMPTY_IDS;
}

interface AnalysisTestSelectionState {
  browseId: string | null;
  tickedIds: string[];
  selectedTestIds: string[]; // stable derived field — never create arrays in selectors
  hiddenIds: string[];
  search: string;
  deviceFilter: string;
  selectorPanelWidth: number;

  setBrowseId: (id: string | null) => void;
  setTickedIds: (ids: string[]) => void;
  setSearch: (search: string) => void;
  setDeviceFilter: (filter: string) => void;
  setSelectorPanelWidth: (width: number) => void;
  clearSelection: () => void;
  hideTest: (id: string) => void;
  clearHiddenTests: () => void;
}

export const useAnalysisTestSelectionStore = create<AnalysisTestSelectionState>()((set) => ({
  browseId: null,
  tickedIds: [],
  selectedTestIds: EMPTY_IDS,
  hiddenIds: [],
  search: '',
  deviceFilter: '',
  selectorPanelWidth: 280,

  setBrowseId: (id) => set((s) => ({
    browseId: id,
    selectedTestIds: deriveSelectedTestIds(s.tickedIds, id, s.hiddenIds),
  })),
  setTickedIds: (ids) => set((s) => ({
    tickedIds: ids,
    selectedTestIds: deriveSelectedTestIds(ids, s.browseId, s.hiddenIds),
  })),
  setSearch: (search) => set({ search }),
  setDeviceFilter: (filter) => set({ deviceFilter: filter }),
  setSelectorPanelWidth: (width) => set({ selectorPanelWidth: Math.min(480, Math.max(160, width)) }),
  clearSelection: () => set({ browseId: null, tickedIds: [], selectedTestIds: EMPTY_IDS }),
  hideTest: (id) => set((s) => {
    if (s.hiddenIds.includes(id)) return s;
    const hiddenIds = [...s.hiddenIds, id];
    const tickedIds = s.tickedIds.filter((x) => x !== id);
    const browseId = s.browseId === id ? null : s.browseId;
    return { hiddenIds, tickedIds, browseId, selectedTestIds: deriveSelectedTestIds(tickedIds, browseId, hiddenIds) };
  }),
  clearHiddenTests: () => set((s) => ({
    hiddenIds: [],
    selectedTestIds: deriveSelectedTestIds(s.tickedIds, s.browseId, []),
  })),
}));

// Returns a stable array reference — safe to use directly in useSyncExternalStore (Zustand).
export const selectSelectedTestIds = (state: AnalysisTestSelectionState): string[] =>
  state.selectedTestIds;

export const selectSelectorPanelWidth = (state: AnalysisTestSelectionState): number =>
  state.selectorPanelWidth;
